import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import { IntlProvider } from "use-intl";
import { DEFAULT_LOCALE, isLocale, type Locale } from "@liftquest/shared/localized";
import de from "./messages/de.json";
import en from "./messages/en.json";

const MESSAGES = { de, en } as const;
const STORAGE_KEY = "liftquest.locale";

function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null; // storage blocked
  }
}

function initialLocale(): Locale {
  const stored = storedLocale();
  if (stored) return stored;
  for (const lang of navigator.languages ?? [navigator.language]) {
    const code = lang.slice(0, 2).toLowerCase();
    if (isLocale(code)) return code;
  }
  return DEFAULT_LOCALE;
}

type LocaleContextValue = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  /** Use the account's language unless this browser has its own choice. */
  adoptAccountLocale: (l: string) => void;
};
const LocaleContext = createContext<LocaleContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // ignore
    }
  }, []);
  const adoptAccountLocale = useCallback((l: string) => {
    if (!storedLocale() && isLocale(l)) setLocaleState(l);
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo(() => ({ locale, setLocale, adoptAccountLocale }), [locale, setLocale, adoptAccountLocale]);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <LocaleContext value={value}>
      <IntlProvider locale={locale} messages={MESSAGES[locale]} timeZone={timeZone}>
        {children}
      </IntlProvider>
    </LocaleContext>
  );
}

export function useLocaleSwitch() {
  const ctx = use(LocaleContext);
  if (!ctx) throw new Error("useLocaleSwitch must be used inside I18nProvider");
  return ctx;
}
